<template>
  <div class="ppv-insights-page">
    <PpvTrackSection
      v-for="group in trackGroups"
      :key="group.track"
      :track-group="group"
      :is-collapsed="!!collapsedTracks[group.track]"
      :expanded-assessments="expandedAssessments"
      :expanded-negatives="!!expandedNegatives[group.track]"
      :track-options="trackOptions"
      @toggle-track="toggleTrack"
      @toggle-expand="toggleAssessment"
      @toggle-negatives="toggleNegatives"
    />

    <div v-if="!trackGroups.length" class="ppv-empty-state">
      <v-icon class="mb-2">{{ icons.mdiChartBar }}</v-icon>
      <p>{{ $t("ppv.insights.empty_state") }}</p>
    </div>
  </div>
</template>

<script>
import icons from '@/mixins/icons'
import { EVENTS, PVI_PANEL_NAME } from '@/plugins/analytics/events'
import { createThrottledTracker } from '@/plugins/analytics/helpers'
import { useInsightsData } from './composables/useInsightsData'
import PpvTrackSection from './insights/PpvTrackSection'

export default {
  name: 'PpvInsightsTable',
  components: { PpvTrackSection },
  mixins: [icons],
  props: {
    visits: {
      type: Array,
      required: true
    },
    allMrxConditions: {
      type: Array,
      default: () => []
    },
    trackOptions: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      collapsedTracks: {},
      expandedNegatives: {},
      expandedAssessments: {},
    }
  },
  computed: {
    trackGroups() {
      const { buildTrackGroups } = useInsightsData(
        { value: this.visits },
        { value: this.allMrxConditions }
      )

      return buildTrackGroups(this.expandedAssessments)
    },
  },
  watch: {
    visits() {
      this.expandedAssessments = {}
      this.expandedNegatives = {}
    },
  },
  created() {
    this.throttledTrack = createThrottledTracker(
      (event, props) => this.$track(event, props),
      500
    )
  },
  methods: {
    toggleTrack(track) {
      this.$set(this.collapsedTracks, track, !this.collapsedTracks[track])
      this.throttledTrack(EVENTS.PV_PATIENT_TRACK_TOGGLED, {
        track_name: track,
        action: this.collapsedTracks[track] ? 'collapse' : 'expand',
        panel_name: PVI_PANEL_NAME,
      })
    },
    toggleNegatives(track) {
      this.$set(this.expandedNegatives, track, !this.expandedNegatives[track])
    },
    toggleAssessment(assessment) {
      const key = `${assessment.track}:${assessment.name}`
      const isExpanded = !!this.expandedAssessments[key]

      if (isExpanded) {
        this.$delete(this.expandedAssessments, key)
      } else {
        this.$set(this.expandedAssessments, key, true)
      }

      this.throttledTrack(EVENTS.PV_CONDITION_ROW_EXPANDED, {
        condition_name: assessment.name,
        track_name: assessment.track,
        action: isExpanded ? 'collapse' : 'expand',
        panel_name: PVI_PANEL_NAME,
      })
    },
  },
}
</script>

<style scoped>
.ppv-insights-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.ppv-empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #c0c8d4;
}
.ppv-empty-state .v-icon {
  color: #c0c8d4 !important;
  font-size: 48px !important;
}
.ppv-empty-state p {
  font-size: 14px;
  margin-top: 8px;
  color: #8a96a8;
}
</style>
